/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `NotificationContext` for the Caintra Connect frontend.
 * @module context/NotificationContext
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { createContext, useContext, useState, useMemo } from "react";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  // const [notifications, setNotifications] = useState([
  //   {
  //     _id: "1",
  //     idUsario: "1",
  //     SK: "1",
  //     modulo: "requirement",
  //     tipo: "newRequirement",
  //     datosMensaje: {
  //       idObjeto: "1",
  //       nombreCompania: "Caintra",
  //     },
  //     fechaDeCreacion: "2025-07-21T09:33:48.384Z",
  //     leido: true
  //   },
  //   {
  //     _id: "2",
  //     idUsario: "1",
  //     SK: "2",
  //     modulo: "requirement",
  //     tipo: "proposalSent",
  //     idObjeto: "2",
  //     datosMensaje: {
  //       idObjeto: "1",
  //       nombreRequerimiento: "[Nombre del requerimiento]",
  //       nombreProveedor: "Caintra",
  //     },
  //     fechaDeCreacion: "2025-07-20T14:42:15.384Z",
  //     leido: false
  //   },
  //   {
  //     _id: "3",
  //     idUsario: "1",
  //     SK: "2",
  //     modulo: "requirement",
  //     tipo: "proposalAccepted",
  //     idObjeto: "2",
  //     datosMensaje: {
  //       idObjeto: "1",
  //       nombreCompania: "Caintra"
  //     },
  //     fechaDeCreacion: "2025-07-14T09:15:28.384Z",
  //     leido: false
  //   },
  //   {
  //     _id: "4",
  //     idUsario: "1",
  //     SK: "2",
  //     modulo: "requirement",
  //     tipo: "proposalRejected",
  //     idObjeto: "2",
  //     datosMensaje: {
  //       idObjeto: "1",
  //       nombreCompania: "Caintra",
  //       nombreRequerimiento: "[Nombre del requerimiento]"
  //     },
  //     fechaDeCreacion: "2025-07-11T09:33:48.384Z",
  //     leido: false
  //   },
  //   {
  //     _id: "5",
  //     idUsario: "1",
  //     SK: "2",
  //     modulo: "requirement",
  //     tipo: "proposalClosed",
  //     idObjeto: "2",
  //     datosMensaje: {
  //       idObjeto: "1",
  //       nombreRequerimiento: "[Nombre del requerimiento]"
  //     },
  //     fechaDeCreacion: "2025-06-21T09:33:48.384Z",
  //     leido: false
  //   },
  //   {
  //     _id: "6",
  //     idUsario: "1",
  //     SK: "2",
  //     modulo: "requirement",
  //     tipo: "proposalsPending",
  //     idObjeto: "2",
  //     datosMensaje: {
  //       idObjeto: "1",
  //       nombreRequerimiento: "[Nombre del requerimiento]"
  //     },
  //     fechaDeCreacion: "2025-05-21T09:33:48.384Z",
  //     leido: false
  //   }
  // ]);

  // Memoize unread count so consumers only update if this value changes
  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.leido).length;
  }, [notifications]);

  // Memoized context value to avoid unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      notifications,
      unreadCount,
      setNotifications,
    }),
    [notifications, unreadCount],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
